if (!customElements.get("pe-bf-product-loop")) {
  customElements.define("pe-bf-product-loop", class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector("[data-bf-track]");
      this.page = this.querySelector("[data-bf-page]");
      if (!this.track || !this.page) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.pages = Number(this.page.dataset.pages) || 1;
      this.total = Number(this.page.dataset.total) || 0;
      this.visible = () => matchMedia("(max-width:749px)").matches ? 2 : 4;
      this.reduced = matchMedia("(prefers-reduced-motion:reduce)");
      this.controls = this.querySelector(".pe-bf-loop-controls");
      this.status = this.querySelector("[data-bf-page-status]");
      this.track.querySelectorAll(":scope > li").forEach(card => { card.dataset.bfBatch = this.page.dataset.page; });
      this.addEventListener("click", event => {
        if (event.target.closest("[data-bf-prev]")) this.move(-1);
        if (event.target.closest("[data-bf-next]")) this.move(1);
      }, options);
      this.track.addEventListener("keydown", event => {
        if (event.target !== this.track || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === "ArrowRight" ? 1 : -1);
      }, options);
      this.track.addEventListener("scroll", () => this.schedule(), { ...options, passive: true });
      this.observer = new ResizeObserver(() => {
        const old = this.step || 1, position = this.track.scrollLeft / old;
        this.step = this.measure();
        this.instant(() => { this.track.scrollLeft = position * this.step; });
      });
      this.observer.observe(this.track);
      this.step = this.measure();
      this.controls.hidden = this.total <= this.visible();
      this.active = this.total > this.visible();
      this.circular = this.active;
      this.warm();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.restoreFrame);
      this.abort = null;
    }
    measure() {
      return (this.track.firstElementChild?.getBoundingClientRect().width || 0) +
        (parseFloat(getComputedStyle(this.track).columnGap) || 0);
    }
    instant(change) {
      cancelAnimationFrame(this.restoreFrame);
      this.track.setAttribute("data-rebasing", "");
      change();
      this.restoreFrame = requestAnimationFrame(() => this.track.removeAttribute("data-rebasing"));
    }
    async warm() {
      if (this.pages > 1) {
        await this.extend(-1);
        await this.extend(1);
      }
    }
    groups() {
      return [...new Set([...this.track.children].map(card => card.dataset.bfBatch))];
    }
    async extend(direction) {
      if (this.loading || this.pages <= 1 || !this.abort) return;
      const groups = this.groups();
      const edge = Number(direction > 0 ? groups.at(-1) : groups[0]);
      const target = direction > 0 ? edge % this.pages + 1 : (edge + this.pages - 2) % this.pages + 1;
      if (groups.includes(String(target))) return;
      const discard = direction > 0 ? groups[0] : groups.at(-1);
      const removable = [...this.track.children].filter(card => card.dataset.bfBatch === discard);
      if (groups.length >= 3 && removable.some(card => card.contains(document.activeElement))) return;
      this.loading = true;
      this.setAttribute("aria-busy", "true");
      try {
        const url = new URL(this.dataset.bfCatalogUrl, location.origin);
        url.searchParams.set("section_id", "pe-bf-products-page");
        url.searchParams.set("page", target);
        const response = await fetch(url, { credentials: "same-origin", signal: this.abort.signal });
        if (!response.ok) throw new Error("Unable to load products");
        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const incoming = [...doc.querySelectorAll("[data-bf-track] > li")];
        if (!incoming.length) throw new Error("Empty product batch");
        incoming.forEach(card => { card.dataset.bfBatch = String(target); });
        const anchor = this.track.scrollLeft;
        this.instant(() => {
          if (direction > 0) this.track.append(...incoming);
          else this.track.prepend(...incoming);
          let nextScroll = anchor + (direction < 0 ? incoming.length * this.step : 0);
          if (groups.length >= 3) {
            removable.forEach(card => card.remove());
            if (direction > 0) nextScroll -= removable.length * this.step;
          }
          this.track.scrollLeft = nextScroll;
        });
        this.status.classList.add("visually-hidden");
        this.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
      } catch (error) {
        if (error.name !== "AbortError") {
          this.status.textContent = "Unable to load more products. Try the arrows again or use View all.";
          this.status.classList.remove("visually-hidden");
        }
      } finally {
        this.loading = false;
        this.removeAttribute("aria-busy");
      }
    }
    schedule() {
      if (this.frame || !this.active) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateStatus();
        const x = this.track.scrollLeft, max = this.track.scrollWidth - this.track.clientWidth;
        if (this.pages === 1) {
          if (document.activeElement !== this.track && this.track.contains(document.activeElement)) return;
          if (x < this.step) this.instant(() => {
            this.track.prepend(this.track.lastElementChild);
            this.track.scrollLeft = x + this.step;
          });
          else if (x > max - this.step) this.instant(() => {
            this.track.append(this.track.firstElementChild);
            this.track.scrollLeft = x - this.step;
          });
        } else if (!this.loading) {
          if (x < this.step * 2) this.extend(-1);
          else if (max - x < this.step * 2) this.extend(1);
        }
      });
    }
    updateStatus() {
      const position = Math.round(this.track.scrollLeft / this.step);
      const card = this.track.children[position];
      if (!card) return;
      this.page.dataset.page = card.dataset.bfBatch;
      const message = "Product " + card.dataset.bfProductIndex + " of " + this.total;
      if (this.status.textContent !== message) this.status.textContent = message;
    }
    async move(direction) {
      if (!this.active || this.loading) return;
      this.track.scrollBy({ left: direction * this.step, behavior: this.reduced.matches ? "instant" : "smooth" });
    }
  });
}
